'use client'

import React, { use } from 'react'
import { useRouter } from 'next/navigation'
import { CircleCheckBig } from 'lucide-react'
import { useSession } from 'next-auth/react'
import { useQueryState } from 'nuqs'
import { parseAsString } from 'nuqs/server'
import {
  Banknote2,
  Bill,
  Checklist,
  DollarBroken,
  RestartBroken,
  WadOfMoneyBroken,
} from 'solar-icons'
import { toast } from 'sonner'
import {
  collectPayment,
  createOrder,
  getLocalRegister,
  setCookieCartId,
} from '@/lib/actions'
import { useCart, useCheckout, useModals, useSearchParams } from '@/lib/hooks'
import { formatPrice } from '@/lib/utils'

import {
  Badge,
  Button,
  Indicator,
  LoadingDots,
  RibbonCard,
} from '@/components/ui'
import { PaymentInput } from '@/components/molecules'
import { RefContext, type Refs } from '@/components/providers/ref-provider'

const presets = [10, 20, 50, 100, 500, 1000, 5000]

export const CollectPayment = () => {
  const router = useRouter()
  const { openModal } = useModals()
  const { totalPayable } = useCart()
  const { orderId, setSearchParams } = useSearchParams()
  const showPaymentConfirmation = localStorage?.getItem(
    'showPaymentConfirmation',
  )
  const [isConfirmingPayment, setIsConfirmingPayment] = React.useState(false)

  const { data: session }: any = useSession()

  const { cart } = useCart()
  const [cartId] = useQueryState('cartId', parseAsString.withDefault(''))
  const {
    balance_to_paid,
    over_paid,
    fully_paid,
    payments_loading,
    total_paid,
    setAmountToCollect,
    setCurrentStep,
    queryParams,
    payments,
    payment_method,
    startNewSale,
    getPayments,
    currentAmountToCollect,
    payment_method_type,
    payment_card_last_digits,
    futurePaymentDate,
    custom_reference,
  } = useCheckout()

  const [inputValue, setInputValue] = React.useState(
    payment_method?.group === 'cash' ? '' : balance_to_paid?.toString() || '',
  )

  const hasPaidSomeAmount = total_paid > 0
  const [isEndingSale, setIsEndingSale] = React.useState(false)
  const [isStartingNewSale, setIsStartingNewSale] = React.useState(false)

  const { amountInputRef } = use(RefContext) as Refs

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    setInputValue('')
    if (amountInputRef.current) {
      amountInputRef.current.value = ''
    }

    setTimeout(() => {
      if (payment_method?.group !== 'cash') {
        // If currentAmountToCollect is available and greater than 0, use it
        const valueToSet =
          currentAmountToCollect && currentAmountToCollect > 0
            ? currentAmountToCollect.toString()
            : balance_to_paid?.toString() || ''

        setInputValue(valueToSet)
        if (amountInputRef.current) {
          amountInputRef.current.value = valueToSet
        }
      }
    }, 100)
  }, [balance_to_paid, payment_method?.group, currentAmountToCollect])

  const quickCollect = async (amount?: number) => {
    setIsConfirmingPayment(true)
    const register: any = await getLocalRegister()

    // Use the passed amount or fall back to currentAmountToCollect
    const amountToCollect = amount ?? currentAmountToCollect

    try {
      const res = await collectPayment({
        code: payment_method_type,
        cart_id: cartId,
        reference_id: Number(custom_reference) ?? null,
        order_id: null,
        status: 'paid', //paid , refund or void(for delete payment)
        currency: 'LKR',
        amount: amountToCollect,
        bin: null, // first 6 digits of card
        last_digits: payment_card_last_digits,
        transaction_id: null,
        payment_method_id: payment_method?.id,
        pos_register_id: register?.id,
        future_payment_date: futurePaymentDate,
        custom_reference: custom_reference
          ? `${custom_reference} - ${payment_method?.name}`
          : `${payment_method?.name} - ${payment_method?.group}`,
      })

      if (res?.error) {
        toast.error(res?.error)
        return
      }

      toast.success(
        `${formatPrice(amountToCollect, 2, true)} collected successfully.`,
      )

      if (amountInputRef?.current) {
        // Add a small delay to ensure the input is properly updated before focusing
        setTimeout(() => {
          amountInputRef?.current?.focus()
        }, 100)

        if (payment_method?.group === 'cash') {
          amountInputRef.current.value = ''
          setInputValue('') // Also update the React state for controlled input
          const event = new Event('input', { bubbles: true })
          amountInputRef.current.dispatchEvent(event)
        } else {
          // Use integer arithmetic to avoid floating point precision issues
          const balanceCents = Math.round(balance_to_paid * 100)
          const collectedCents = Math.round(amountToCollect * 100)
          const remainingCents = balanceCents - collectedCents
          const remainingBalance = (remainingCents / 100).toFixed(2)
          amountInputRef.current.value = remainingBalance
          setInputValue(remainingBalance) // Also update the React state for controlled input
          // Trigger input event to ensure React state is updated
          const event = new Event('input', { bubbles: true })
          amountInputRef.current.dispatchEvent(event)
        }
      }

      getPayments({ cartId })
    } catch (error: any) {
      if (typeof error === 'string') {
        toast.error(error)
        return
      }

      toast.error(error?.message)
      return
    } finally {
      setIsConfirmingPayment(false)
    }
  }

  React.useEffect(() => {
    if (amountInputRef?.current) {
      amountInputRef.current.focus()
    }
  }, [amountInputRef])

  React.useEffect(() => {
    // Fix floating point precision when setting input value
    // Only set value for non-cash payment methods
    if (payment_method?.group !== 'cash') {
      const formattedValue = balance_to_paid
        ? Number(balance_to_paid).toFixed(2)
        : ''
      setInputValue(formattedValue)
    }
  }, [balance_to_paid, payment_method?.group])

  const handleOnCollect = (data: FormData) => {
    const amount = data.get('amount')

    if (!amount || Number(amount) <= 0) {
      toast.error('Invalid amount')
      return
    }

    setAmountToCollect(Number(amount))

    if (showPaymentConfirmation === 'true') {
      openModal('CONFIRM_PAYMENT')
    } else {
      quickCollect(Number(amount))
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    handleOnCollect(formData)
  }

  const handleNewSale = async () => {
    setIsStartingNewSale(true)

    if (session?.locationType && session?.locationType === 'restaurant') {
      openModal('SELECT_NEW_ORDER_OPTIONS')
    } else {
      const cart = await startNewSale()
      if (!cart) {
        toast.error('Failed to start a new sale')
        return
      }
      await setCookieCartId(cart.id)
      window.location.href = `/sale?cartId=${cart.id}`
    }
  }

  const handleEndSale = async () => {
    if (orderId && fully_paid) {
      toast.error('Sale already ended')
      return null
    }

    if (!queryParams?.cartId) {
      toast.error('No cart found')
      return
    }

    // Check if cart has any products without warranty serial number
    const hasNoWarrentyProducts = cart?.cart_items?.some(
      (item: any) =>
        (item?.has_warranty === 1 || item?.has_warranty === true) &&
        !item?.warranty?.serial_number,
    )

    if (hasNoWarrentyProducts) {
      openModal('CONFIRM_NO_WARRANTY')
      return
    }

    setIsEndingSale(true)

    const order = await createOrder({
      cart_id: Number(queryParams.cartId),
      customer_id: queryParams?.customerId,
    })

    await setCookieCartId('')

    setIsEndingSale(false)
    setSearchParams({ orderId: order?.id })

    toast.success('Sale ended successfully')

    openModal('PRINT_RECEIPT')
  }

  return (
    <div className="flex h-full w-full flex-col items-center gap-4">
      <div className="flex w-full flex-grow flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-3">
          <div className="flex w-full items-center justify-center gap-2">
            <h2 className="text-base font-medium">Collect Payment by</h2>
            <Badge
              size="lg"
              variant="outline"
              className="min-w-none h-auto cursor-pointer truncate px-2.5 pb-0.5 pt-1 text-[13px]"
              onClick={() => {
                setAmountToCollect(0)
                setCurrentStep('SELECT_PAYMENT_METHOD')
              }}
            >
              <Indicator variant="neutral" />
              {payment_method?.name}
            </Badge>
          </div>

          <h1 className="font-mono text-3xl font-extrabold leading-[1] lg:text-4xl">
            {formatPrice(balance_to_paid, 2, true)}
          </h1>
        </div>

        <form
          className="-mx-10 flex w-full flex-col items-center gap-4"
          onSubmit={handleFormSubmit}
        >
          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center gap-1">
              <PaymentInput
                name="amount"
                defaultValue={
                  payment_method?.group === 'cash'
                    ? ''
                    : balance_to_paid?.toString() || ''
                }
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                }}
                ref={amountInputRef}
                disabled={isConfirmingPayment}
                isLoading={isConfirmingPayment}
                postfix={
                  <Button
                    variant="outline"
                    size="default"
                    className="hidden h-full gap-2 rounded-md md:flex"
                    onClick={() => setInputValue('')}
                    disabled={isConfirmingPayment}
                  >
                    <span>Reset</span>
                    <RestartBroken className="size-5" />
                  </Button>
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex w-full flex-wrap items-center gap-1">
                {presets.map((preset) => (
                  <Button
                    key={preset}
                    variant="secondary"
                    size="default"
                    className="flex-1"
                    onClick={() => {
                      const currentValue =
                        Number.parseFloat(inputValue.replace(/,/g, '')) || 0
                      // Use integer arithmetic to avoid floating point precision issues
                      const currentCents = Math.round(currentValue * 100)
                      const presetCents = preset * 100
                      const newCents = currentCents + presetCents
                      const newValue = (newCents / 100).toFixed(2)
                      setInputValue(newValue)

                      if (amountInputRef?.current) {
                        amountInputRef.current.focus()
                      }
                    }}
                    disabled={isConfirmingPayment}
                  >
                    {formatPrice(preset, 2, false, false)}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <RibbonCard
            ribbonClassName="after:bg-gray-50 before:bg-gray-50"
            listClassName="text-gray-950"
            highlightsClassName="text-gray-950 text-sm"
            isLoading={payments_loading}
            list={[
              {
                label: 'Total Payable',
                value: formatPrice(totalPayable, 2, true),
              },
              {
                label: 'Customer Paid',
                value: formatPrice(total_paid, 2, true),
              },
            ]}
            highlightList={[
              {
                label: 'Balance to Collect',
                value: formatPrice(balance_to_paid, 2, true),
                className:
                  balance_to_paid > 0 ? 'text-red-500' : 'text-gray-950',
              },
              {
                label: 'Balance to Customer',
                value: formatPrice(over_paid, 2, true),
                className: hasPaidSomeAmount
                  ? 'text-green-500'
                  : 'text-gray-950',
              },
            ]}
          />
        </form>
      </div>

      <div className="flex w-full flex-col gap-2">
        {/* EndSale and View Payments */}
        {!orderId && fully_paid && (
          <div className="flex w-full flex-col items-center gap-2 md:flex-row">
            <ViewPaymentsButton
              payments={payments}
              openModal={() => openModal('VIEW_CART_PAYMENTS')}
            />
            <EndSaleButton
              isEndingSale={isEndingSale}
              handleEndSale={handleEndSale}
            />
          </div>
        )}

        {/* Start New Sale  & Print Receipt */}
        {orderId && fully_paid && (
          <div className="flex w-full flex-col items-center gap-2 md:flex-row">
            <PrintReceiptButton
              isEndingSale={isEndingSale}
              openModal={() => openModal('PRINT_RECEIPT')}
            />
            <StartNewSaleButton
              isStartingNewSale={isStartingNewSale}
              handleNewSale={handleNewSale}
            />
          </div>
        )}

        {!fully_paid && !orderId && (
          <div className="flex w-full flex-col items-center gap-2 md:flex-row">
            <ChangePaymentMethodButton
              setCurrentStep={() => setCurrentStep('SELECT_PAYMENT_METHOD')}
            />
            <ViewPaymentsButton
              payments={payments}
              openModal={() => openModal('VIEW_CART_PAYMENTS')}
            />
          </div>
        )}
      </div>
    </div>
  )
}

const ChangePaymentMethodButton = ({
  setCurrentStep,
}: {
  setCurrentStep: (step: string) => void
}) => {
  return (
    <Button
      variant="outline"
      className="w-full justify-start gap-2"
      size="lg"
      onClick={() => setCurrentStep('SELECT_PAYMENT_METHOD')}
    >
      <div className="flex flex-grow items-center justify-center gap-2">
        <Banknote2 strokeWidth={1.2} className="size-5" />
        Change Payment Method
      </div>
    </Button>
  )
}

const PrintReceiptButton = ({
  isEndingSale,
  openModal,
}: {
  isEndingSale: boolean
  openModal: (modal: string) => void
}) => {
  return (
    <Button
      disabled={isEndingSale}
      variant="outline"
      className="w-full justify-start gap-2"
      size="lg"
      onClick={() => openModal('PRINT_RECEIPT')}
    >
      <div className="flex flex-grow items-center justify-center gap-2">
        <Bill strokeWidth={1.2} className="size-5" />
        Print Receipt
      </div>
    </Button>
  )
}

const EndSaleButton = ({
  isEndingSale,
  handleEndSale,
}: {
  isEndingSale: boolean
  handleEndSale: () => void
}) => {
  return (
    <Button
      variant="brand"
      className="w-full justify-center gap-2"
      size="lg"
      onClick={handleEndSale}
      disabled={isEndingSale}
    >
      {isEndingSale && <LoadingDots dotClassName="bg-white" />}
      {!isEndingSale && (
        <div className="flex flex-grow items-center justify-center gap-2">
          <CircleCheckBig strokeWidth={1.2} className="size-5" />
          End Sale
        </div>
      )}
    </Button>
  )
}

const ViewPaymentsButton = ({
  payments,
  openModal,
}: {
  payments: any
  openModal: (modal: string) => void
}) => {
  return (
    <Button
      variant="outline"
      className="w-full justify-start gap-2"
      size="lg"
      onClick={() => openModal('VIEW_CART_PAYMENTS')}
    >
      <div className="flex flex-grow items-center gap-2">
        <Checklist strokeWidth={1.2} className="size-5" />
        View Payments
      </div>
      <Badge variant="secondary" size="lg">
        {payments?.length}
      </Badge>
    </Button>
  )
}

const StartNewSaleButton = ({
  isStartingNewSale,
  handleNewSale,
}: {
  isStartingNewSale: boolean
  handleNewSale: () => void
}) => {
  return (
    <Button
      variant="brand"
      className="w-full justify-start gap-2"
      size="lg"
      onClick={handleNewSale}
      disabled={isStartingNewSale}
    >
      {isStartingNewSale && <LoadingDots dotClassName="bg-white" />}
      {!isStartingNewSale && (
        <div className="flex flex-grow items-center justify-center gap-2">
          <CircleCheckBig strokeWidth={1.2} className="size-5" />
          Start New Sale
        </div>
      )}
    </Button>
  )
}
